import { useQuery } from 'react-query';

import { CACHE_KEYS, CACHE_TIMES } from '.';
import { api } from './swaggerApi';

export const useTapQuestionResult = ({
  tapQuestionIdentifier,
  filters,
  groupAnswers = false,
  isRunning = false,
  campaignResultIdentifier,
}: {
  tapQuestionIdentifier: string;
  filters?: Record<string, string[]>;
  groupAnswers?: boolean;
  isRunning?: boolean;
  campaignResultIdentifier?: string;
}) => {
  return useQuery(
    [
      CACHE_KEYS.SINGLE_SURVEY_QUESTION_RESULTS,
      tapQuestionIdentifier,
      groupAnswers,
      { ...(filters || {}) },
    ],
    async () => {
      const { data: { data: results } = {} } =
        await api.insightsPlatform.tapQuestionsResultsDetail({
          identifier: tapQuestionIdentifier,
          filters,
          group_answers: groupAnswers,
          ...(!!campaignResultIdentifier && {
            result_identifier: campaignResultIdentifier,
          }),
        });

      return results;
    },
    {
      enabled: !!tapQuestionIdentifier,
      keepPreviousData: false,
      refetchInterval: isRunning
        ? CACHE_TIMES.TWO_MINUTES
        : CACHE_TIMES.FIVE_MINUTES,
    }
  );
};

export const useTapQuestionCrossTabResult = ({
  tapQuestionIdentifier,
  crossTabQuestionId,
  filters,
  groupAnswers = false,
  isRunning = false,
  campaignResultIdentifier,
}: {
  tapQuestionIdentifier: string;
  crossTabQuestionId?: string;
  filters?: Record<string, string[]>;
  groupAnswers?: boolean;
  isRunning?: boolean;
  campaignResultIdentifier?: string;
}) => {
  return useQuery(
    [
      CACHE_KEYS.SINGLE_SURVEY_CROSS_TAB_QUESTION_RESULTS,
      tapQuestionIdentifier,
      crossTabQuestionId,
      groupAnswers,
      { ...(filters || {}) },
    ],
    async () => {
      const { data: { data: results } = {} } =
        await api.insightsPlatform.tapQuestionsCrossTabResultsDetail({
          identifier: tapQuestionIdentifier,
          cross_tab_question_id: crossTabQuestionId,
          filters,
          group_answers: groupAnswers,
          ...(!!campaignResultIdentifier && {
            result_identifier: campaignResultIdentifier,
          }),
        });
      return results;
    },
    {
      enabled: !!tapQuestionIdentifier,
      keepPreviousData: false,
      refetchInterval: isRunning
        ? CACHE_TIMES.TWO_MINUTES
        : CACHE_TIMES.FIVE_MINUTES,
    }
  );
};

export const useQuestionTapAnswersList = (
  tapQuestionIdentifier: string,
  campaignResultIdentifier: string
) => {
  return useQuery(
    [CACHE_KEYS.QUESTION_TAP_ANSWERS_LIST, tapQuestionIdentifier],
    async () => {
      const { data: { data: tapAnswers } = {} } =
        await api.insightsPlatform.tapQuestionsTapAnswersListDetail({
          identifier: tapQuestionIdentifier,
          ...(!!campaignResultIdentifier && {
            result_identifier: campaignResultIdentifier,
          }),
        });
      return tapAnswers;
    },
    {
      enabled: !!tapQuestionIdentifier,
      keepPreviousData: false,
      staleTime: CACHE_TIMES.SIXTY_MINUTES,
    }
  );
};

export const useQualificationAnswerLabels = ({
  questionId,
  countryId,
  languageId,
  campaignResultIdentifier,
}: {
  questionId: string;
  countryId: string;
  languageId: string;
  campaignResultIdentifier?: string;
}) => {
  return useQuery(
    [CACHE_KEYS.QUALIFICATION_ANSWER_LABELS, questionId, countryId, languageId],
    async () => {
      const { data: { data: labels } = {} } =
        await api.insightsPlatform.qualificationAnswerLabelsList({
          question_id: questionId,
          country_id: countryId,
          language_id: languageId,
          ...(!!campaignResultIdentifier && {
            result_identifier: campaignResultIdentifier,
          }),
        });
      return labels.reduce<Record<string, string>>((acc, label) => {
        return { ...acc, [label.attributes.key]: label.attributes.label };
      }, {});
    },
    {
      enabled: !!questionId && !!countryId && !!languageId,
      keepPreviousData: true,
      staleTime: CACHE_TIMES.SIXTY_MINUTES,
    }
  );
};
